import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import format from "date-fns/format";
import { get, post } from "../../api";
import {
  ADD_TO_BASKET_URL,
  ASSIGN_ASSORT_TO_BASKET_URL,
  GET_BASKET_URL,
  GET_CALCULATE_PROM,
  RESET_PROMOTION,
} from "../../api/apiRoutes";
import { BasketItem, setBasketItemExercise, setBasketItemLimited } from "../../models/BasketItem";
import { TicketAccess } from "../../models/TicketAccess";
import { Assort } from "../../models/Assort";
import { SelectedSeats } from "../../models/SelectedSeats";
import { QntStateItem } from "../../models/QntStateItem";
import getId from "../../../utils/getId";
import { replaceWithGuestSession } from "../auth/authActions";
import { NO_LOGGED_ERROR_MESSAGE, UNKNOWN_ERROR } from "../../utils/apiErrorMessages";
import { clearSurveyDraft } from "../../components/FinalizePage/BasketSurveyStep/utils/surveyDraftStorage";
import {
  ADD_BASKET_ITEM,
  ADD_LIMITED_BASKET_ITEM,
  APPLY_PROMO_CODE,
  ASSIGN_ASSORT,
  CLEAR_BASKET,
  FETCH_BASKET,
  PROLONG_BASKET_PASS,
  RESET_BASKET,
  RESET_PROMO,
  SET_BASKET_ADD_ERROR,
  SET_CURRENT_PROMO_CODE,
  SET_PROMO_MESSAGE,
  UPDATE_BASKET_LENGTH,
  UPDATE_CALENDAR_SALE_QNT_STATE,
  UPDATE_SELECT_SEATS,
} from "./basketTypes";

type AppState = {
  config: {
    facilityIds: string;
  };
  auth: {
    userToken?: {
      sessionToken?: string;
    };
  };
};

const facilityIdsFrom = (getState: () => unknown) => (getState() as AppState).config.facilityIds;

const isNotLoggedError = (errorTable: unknown) =>
  Array.isArray(errorTable) && errorTable.some((item) => typeof item === "string" && item.includes(NO_LOGGED_ERROR_MESSAGE));

const settle = async (request: Promise<any>) => {
  try {
    return await request;
  } catch (error: any) {
    if (error?.name === "AbortError") {
      return { isError: true, errorTable: [] as string[] };
    }
    return { isError: true, errorTable: [UNKNOWN_ERROR] };
  }
};

const today = () => format(new Date(), "yyyy-MM-dd");

export const updateSelectedSeats = createAction<SelectedSeats[]>(UPDATE_SELECT_SEATS);
export const updateBasketLength = createAction<number>(UPDATE_BASKET_LENGTH);
export const updateCalendarSaleQntState = createAction<QntStateItem[]>(UPDATE_CALENDAR_SALE_QNT_STATE);
export const setPromoMessage = createAction<string>(SET_PROMO_MESSAGE);
export const setBasketAddError = createAction<string>(SET_BASKET_ADD_ERROR);
export const setCurrentPromoCode = createAction<string>(SET_CURRENT_PROMO_CODE);
export const resetBasket = createAction<void>(RESET_BASKET);

let basketRequest: Promise<any> | null = null;
let basketRequestToken = "";
let basketRequestGen = 0;
let basketMutationGen = 0;

const bumpBasketMutationGen = () => {
  basketMutationGen += 1;
};

export const fetchBasket = createAsyncThunk(
  FETCH_BASKET,
  async (_arg: { skipLoading?: boolean } | undefined, { dispatch, getState }) => {
    const sessionToken = (getState() as AppState).auth.userToken?.sessionToken || "";
    const startedGen = basketMutationGen;
    const canReuseInFlight =
      !!basketRequest && basketRequestToken === sessionToken && basketRequestGen === startedGen;

    if (!canReuseInFlight) {
      basketRequestToken = sessionToken;
      basketRequestGen = startedGen;
      const requestToken = sessionToken;
      const requestGen = startedGen;
      basketRequest = settle(get(GET_BASKET_URL)).finally(() => {
        if (basketRequestToken === requestToken && basketRequestGen === requestGen) {
          basketRequest = null;
        }
      });
    }
    const response = await basketRequest;
    const currentToken = (getState() as AppState).auth.userToken?.sessionToken || "";
    if (currentToken !== sessionToken || basketMutationGen !== startedGen) {
      return { ignored: true };
    }
    if (response?.isError && isNotLoggedError(response.errorTable)) {
      dispatch(replaceWithGuestSession());
    }
    return response;
  }
);

type AddBasketItemArg = {
  assortIds: string;
  price: number;
  ticketAccess?: TicketAccess | null;
  validFrom?: Date | string | null;
  qnt?: number;
};

export const addBasketItem = createAsyncThunk(ADD_BASKET_ITEM, async (arg: AddBasketItemArg, { dispatch, getState }) => {
  const facilityIds = facilityIdsFrom(getState);
  let formattedDate = today();
  if (arg.validFrom) {
    formattedDate = format(new Date(arg.validFrom), "yyyy-MM-dd");
  }
  const quantity = arg.qnt ? arg.qnt : 1;
  const basketItem =
    arg.ticketAccess === null
      ? setBasketItemLimited(facilityIds, arg.assortIds, formattedDate, quantity, null)
      : setBasketItemExercise(facilityIds, arg.assortIds, arg.price, arg.ticketAccess, formattedDate, quantity);
  const response = await settle(post(ADD_TO_BASKET_URL, { ...basketItem }));
  if (!response?.isError) {
    bumpBasketMutationGen();
    await dispatch(fetchBasket({ skipLoading: true }));
  }
  return response;
});

type AddLimitedArg = {
  assort: Assort;
  qnt: number;
  validFrom?: Date | string | null;
};

export const addLimitedBasketItem = createAsyncThunk(
  ADD_LIMITED_BASKET_ITEM,
  async (arg: AddLimitedArg, { dispatch, getState }) => {
    const facilityIds = facilityIdsFrom(getState);
    let formattedDate = today();
    if (arg.validFrom) {
      formattedDate = format(new Date(arg.validFrom), "yyyy-MM-dd");
    }
    const quantity = arg.qnt ? arg.qnt : 1;
    const basketItem = setBasketItemLimited(facilityIds, getId(arg.assort), formattedDate, quantity, []);
    const response = await settle(post(ADD_TO_BASKET_URL, { ...basketItem }));
    if (!response?.isError) {
      bumpBasketMutationGen();
      await dispatch(fetchBasket({ skipLoading: true }));
    }
    return response;
  }
);

type ProlongPassArg = {
  assort: string;
  qnt: number;
  validFrom?: Date | string | null;
};

export const prolongBasketPass = createAsyncThunk(
  PROLONG_BASKET_PASS,
  async (arg: ProlongPassArg, { dispatch, getState }) => {
    const facilityIds = facilityIdsFrom(getState);
    let formattedDate = today();
    if (arg.validFrom) {
      formattedDate = format(new Date(arg.validFrom), "yyyy-MM-dd");
    }
    const quantity = arg.qnt ? arg.qnt : 1;
    const response = await settle(
      post(ADD_TO_BASKET_URL, {
        ...setBasketItemLimited(facilityIds, arg.assort, formattedDate, quantity, []),
      })
    );
    if (!response?.isError) {
      bumpBasketMutationGen();
      await dispatch(fetchBasket());
    }
    return response;
  }
);

export const assignAssort = createAsyncThunk(
  ASSIGN_ASSORT,
  async (arg: { assortIds: string; basketItem: BasketItem }, { dispatch, getState }) => {
    const facilityIds = facilityIdsFrom(getState);
    const response = await settle(
      post(ASSIGN_ASSORT_TO_BASKET_URL, {
        ...arg.basketItem,
        assortIds: arg.assortIds,
        facilityIds,
        qnt: 1,
      })
    );
    if (!response?.isError) {
      bumpBasketMutationGen();
      await dispatch(fetchBasket());
    }
    return response;
  }
);

export const applyPromoCode = createAsyncThunk(APPLY_PROMO_CODE, async (code: string, { dispatch, getState }) => {
  const facilityIds = facilityIdsFrom(getState);
  const response = await settle(get(GET_CALCULATE_PROM(code, facilityIds)));
  if (!response?.isError) {
    bumpBasketMutationGen();
    dispatch(setCurrentPromoCode(code));
    await dispatch(fetchBasket());
  }
  return response;
});

export const resetPromo = createAsyncThunk(RESET_PROMO, async (_, { dispatch, getState }) => {
  const facilityIds = facilityIdsFrom(getState);
  try {
    const response = await settle(get(RESET_PROMOTION("", facilityIds)));
    if (!response?.isError) {
      bumpBasketMutationGen();
      await dispatch(fetchBasket({ skipLoading: true }));
    }
    return response;
  } finally {
    dispatch(setCurrentPromoCode(""));
  }
});

export const clearBasket = createAsyncThunk(CLEAR_BASKET, async (_, { getState }) => {
  clearSurveyDraft(facilityIdsFrom(getState));
  return true;
});
